/**
 * 결제 수단 조합 계산 헬퍼 (#217/#218, β-2b).
 *
 * admin BE 공개 카탈로그 (`GET /api/v1/payment-method-combinations`) 5 조합과
 * 매장의 `accepted_payment_methods` (Store 리소스) 를 JOIN 하여 CheckoutPage 에
 * 렌더링할 "매장 활성 + 현재 단계 허용" 조합만 반환.
 *
 * 스펙: `.claude/specs/SPEC-217-218-payment-methods-extension.md` §3.4 (fail-closed)
 *
 * 설계 리뷰 code-reviewer P1-1 반영: `isCardChannel` / `isCashChannel` 판정 헬퍼 포함 —
 * CheckoutPage 내 조건 분기가 `combo.method === 'card'` 리터럴 비교 대신 함수 사용.
 */
import type {
  PaymentMethodCombination,
  PaymentMethodKind,
  StorePaymentMethodAssignment,
} from '@/lib/api/types';

/**
 * #421 [2]: 조합 카탈로그 method 축의 전체 kind 목록 (런타임 SSOT).
 *
 * `Record<PaymentMethodKind, true>` 를 경유해 컴파일 타임 완전성을 강제한다 —
 * `PaymentMethodKind` 유니온에 신규 method(예: 'transfer')가 추가되면 이 Record 에
 * 키 누락 타입 에러가 발생하므로, 타입 확장 + 이 한 곳만 갱신하면
 * `computeMethodSupport` 를 소비하는 모든 derive 에 자동 전파된다.
 *
 * 주의: `PaymentMethodKind` 는 BE `payment_method_combinations.method` PG enum
 * ('card' | 'cash') 과 1:1 계약 — BE enum 확장 전에 FE 가 선제 확장하지 않는다.
 */
const METHOD_KIND_REGISTRY: Record<PaymentMethodKind, true> = {
  card: true,
  cash: true,
};

export const PAYMENT_METHOD_KINDS = Object.keys(
  METHOD_KIND_REGISTRY,
) as readonly PaymentMethodKind[];

/**
 * 매장 가용 조합(availableCombinations)에서 method(kind)별 지원 여부 derive (#421 [2]).
 *
 * CheckoutPage 의 `cashSupported` / `cardSupported` 개별 `some(method === ...)` 리터럴
 * 중복을 Map 패턴으로 일반화 — 신규 method 추가 시 UI derive 분기를 일일이 수정하지
 * 않아도 지원 여부 산출은 자동 포함된다.
 *
 * fail-closed: 빈 조합 배열이면 모든 kind 가 false (기존 derive 와 동일 의미).
 */
export function computeMethodSupport(
  combinations: PaymentMethodCombination[],
): ReadonlyMap<PaymentMethodKind, boolean> {
  const presentKinds = new Set(combinations.map((combo) => combo.method));

  return new Map(
    PAYMENT_METHOD_KINDS.map((kind) => [kind, presentKinds.has(kind)]),
  );
}

/**
 * 결제 조합 필터에 적용할 주문유형. admin `OrderType` (pickup/table) 와 정합.
 *
 * CheckoutPage 는 `'pickup' | 'delivery' | 'table'` 을 다루지만, delivery 는
 * `SUPPORTED_ORDER_TYPES` 에서 pickup 으로 폴백되므로 필터 입력은 pickup/table 만 받는다.
 */
export type PaymentFilterOrderType = 'pickup' | 'table';

/**
 * 할당의 order_type scope 가 주어진 주문유형에 노출되어야 하는지 판정.
 *
 * admin `PaymentAssignmentScope::valuesForOrderType` (`order_type IN ('all', :orderType)`)
 * 와 동일 의미:
 *   - `'all'`: 모든 주문유형에 노출
 *   - 현재 orderType 과 정확히 일치: 노출
 *   - 그 외(다른 유형 / 인식 불가 값): 미노출 (fail-closed)
 *
 * fail-closed 경계 (Codex P2):
 *   - `undefined`(필드 부재, R1 배포 이전 / stale 캐시): `'all'` 로 간주 → 무회귀(모든 유형 노출).
 *     R1 백필이 'all' 이므로 실제 API 응답은 명시적으로 이 분기를 타지 않는다.
 *   - `null`(명시적 null) / 인식 불가 값: `'all'` 로 간주하지 **않고** fail-closed → 미노출.
 *     `?? 'all'` 은 null 까지 흡수하므로 `=== undefined` 분기로 부재만 좁게 처리.
 */
function assignmentAppliesToOrderType(
  assignment: StorePaymentMethodAssignment,
  orderType: PaymentFilterOrderType,
): boolean {
  const scope =
    assignment.order_type === undefined ? 'all' : assignment.order_type;

  return scope === 'all' || scope === orderType;
}

/**
 * 매장이 허용한 조합 중 **활성 카탈로그** 에 존재하고 **주문유형에 노출** 되는 조합만 반환.
 *
 * fail-closed: assigned 배열이 undefined/empty 이면 빈 배열 반환 → CheckoutPage 가
 * "결제 준비 중" 배너 노출.
 *
 * order_type 필터(#1705 R1 / D1-a 계약 소비): 각 할당의 `order_type` scope 가
 * `'all'` 이거나 현재 `orderType` 과 일치할 때만 포함. admin 의
 * `where order_type IN ('all', orderType)` 와 동일 의미.
 *
 * @param catalog API 로 조회한 전체 활성 조합 (usePaymentMethodCombinations)
 * @param assigned 매장 Store 응답의 `accepted_payment_methods` 배열
 * @param orderType 현재 주문유형(pickup/table). 미지정 시 order_type 필터 미적용
 *   (기존 호출자 무회귀 — 매장의 모든 활성 할당 노출).
 */
export function computeAvailableCombinations(
  catalog: PaymentMethodCombination[],
  assigned: StorePaymentMethodAssignment[] | undefined,
  orderType?: PaymentFilterOrderType,
): PaymentMethodCombination[] {
  const platformPrepay = catalog.find((combo) => combo.code === 'card_platform_prepay');

  if (orderType === 'pickup') {
    return platformPrepay === undefined ? [] : [platformPrepay];
  }

  if (!assigned || assigned.length === 0) {
    return orderType === 'table' && platformPrepay !== undefined ? [platformPrepay] : [];
  }

  const scopedAssignments =
    orderType === undefined
      ? assigned
      : assigned.filter((a) => assignmentAppliesToOrderType(a, orderType));

  const assignedIds = new Set(scopedAssignments.map((a) => a.combination_id));
  if (orderType === 'table' && platformPrepay !== undefined) {
    // #759: 매장 할당(card_platform_prepay 제외)의 timing 을 조회해 후불 전용 여부 판정.
    // catalog 에 없는 assignment 는 timing 불명 → postpay-only 판정에 기여하지 않음.
    // assignedTimings 가 비거나 prepay 가 섞이면 isPostpayOnly=false → prepay 추가(fail-closed).
    // 후불 전용(postpay 있고 prepay 없음)만 card_platform_prepay 를 제외한다 (설계 §4-C).
    const assignedTimings = scopedAssignments
      .map((a) => catalog.find((c) => c.id === a.combination_id))
      .filter(
        (c): c is PaymentMethodCombination =>
          c !== undefined && c.code !== 'card_platform_prepay',
      )
      .map((c) => c.timing);
    const isPostpayOnly =
      assignedTimings.includes('postpay') && !assignedTimings.includes('prepay');
    if (!isPostpayOnly) {
      assignedIds.add(platformPrepay.id);
    }
  }

  return catalog
    .filter((combo) => assignedIds.has(combo.id))
    .sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * Card 채널 (Bloomin/OpenLinea 게이트웨이 경유) 여부.
 * `requires_gateway` 만으로는 부족 — 미래 카드 외 gateway 조합 대비.
 * Codex 설계 P1 축3 반영: `combo.code === 'card_platform_prepay'` 정확 매칭.
 */
export function isPlatformCardCombination(
  combo: PaymentMethodCombination,
): boolean {
  return combo.code === 'card_platform_prepay';
}

/**
 * Card 수단 (채널 무관) 여부. saved_card 분기 등에 사용.
 */
export function isCardMethodCombination(
  combo: PaymentMethodCombination,
): boolean {
  return combo.method === 'card';
}

/**
 * Cash 수단 여부. 포인트 미지원 등에 사용.
 */
export function isCashMethodCombination(
  combo: PaymentMethodCombination,
): boolean {
  return combo.method === 'cash';
}
